import React from "react";
import { cn } from "@/lib/utils";
import { resolveMiniStatGradient } from "@/utils/miniStatGradient";
import TradeLogLiftedTitleCard from "./TradeLogLiftedTitleCard";

interface TradeLogMiniStatisticsProps {
  title: string;
  amount: string;
  percentage: number | null;
  amountColor?: string;
  backgroundGradient?: string;
  timeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
  showPercentage?: boolean;
  showTimeframe?: boolean;
  tooltip?: string;
  /** Fill parent height (stacked column with equal thirds). */
  fillColumn?: boolean;
  /**
   * Prefer fitting the full amount (wrap + slightly smaller type) instead of
   * single-line ellipsis — useful in dense notification / drawer grids.
   */
  fitAmount?: boolean;
  /**
   * Multi-word status labels (e.g. “Still recovering”) use smaller, non-numeric
   * type so they stay readable inside mini-stat surfaces.
   */
  amountAsLabel?: boolean;
}

const TradeLogMiniStatistics: React.FC<TradeLogMiniStatisticsProps> = ({
  title,
  amount,
  percentage,
  amountColor,
  backgroundGradient,
  timeframe = "all-time",
  onTimeframeChange,
  showPercentage = true,
  tooltip,
  fillColumn = false,
  fitAmount = false,
  amountAsLabel = false,
}) => {
  const autoGradient = resolveMiniStatGradient({ amount, percentage, backgroundGradient });

  const textColor = autoGradient ? "white" : (amountColor || undefined);

  return (
    <TradeLogLiftedTitleCard
      title={title}
      tooltip={tooltip}
      timeframe={timeframe}
      onTimeframeChange={onTimeframeChange}
      backgroundGradient={autoGradient}
      height={fitAmount || amountAsLabel ? "auto" : fillColumn ? "100%" : undefined}
    >
      <div
        data-dashboard-mini-stat-surface=""
        data-variant={autoGradient ? "gradient" : "muted"}
        className={cn(
          "relative m-0 flex h-full min-h-0 w-full flex-1 items-center border-none",
          fitAmount || amountAsLabel ? "overflow-visible" : "overflow-hidden",
          !autoGradient && "bg-muted",
        )}
        style={autoGradient ? { background: autoGradient } : undefined}
      >
        {autoGradient ? (
          <div
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: "40px 40px",
            }}
            aria-hidden
          />
        ) : null}
        <div
          data-trade-log-mini-stat-body=""
          className={cn(
            "relative z-[2] m-0 flex w-full min-w-0 items-center justify-between px-1.5 sm:px-2 md:px-2.5",
            fitAmount || amountAsLabel ? "overflow-visible py-0.5" : "overflow-hidden",
          )}
        >
          <span
            className={cn(
              "m-0 max-w-full min-w-0 flex-1 font-ibm-plex font-medium leading-tight",
              amountAsLabel
                ? "whitespace-normal break-words text-base sm:text-lg md:text-xl"
                : fitAmount
                  ? "break-words text-xl sm:text-2xl md:text-3xl"
                  : "truncate text-2xl sm:text-3xl md:text-4xl",
            )}
            style={{ color: textColor }}
            title={fitAmount || amountAsLabel ? undefined : amount}
          >
            {amount}
          </span>
          {showPercentage && percentage !== null ? (
            <span
              data-dashboard-mini-stat-comparison-badge=""
              className="ml-2 inline-flex shrink-0 items-center rounded-[4px] border border-solid px-1.5 py-px font-ibm-plex text-[10px] font-medium leading-none sm:ml-2.5 sm:px-[7px] sm:py-0.5 sm:text-[11px] md:ml-3 md:px-2 md:py-0.5 md:text-xs"
              style={{
                color: autoGradient ? "white" : (amountColor || undefined),
                borderColor: autoGradient ? "white" : (amountColor || undefined),
                backgroundColor: "transparent",
              }}
            >
              {percentage >= 0 ? "+" : ""}
              {percentage.toFixed(2)}%
            </span>
          ) : null}
        </div>
      </div>
    </TradeLogLiftedTitleCard>
  );
};

export default TradeLogMiniStatistics;
